/**
 * Transform functions for Basic Life benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields)
 * - Backend API format (nested objects)
 */

import { BaseBasicLifeAdndBenefitBenefitMaximumType } from "@platform/openapi-types";

import {
  cleanupNotDisclosedBenefits,
  shouldIncludeBenefitInApi,
  toExtendedStringField,
  transformAgeReductionScheduleFromApi,
  transformAgeReductionScheduleToApi,
  transformTypeAmountFromApi,
  transformTypeAmountToApi,
  transformTypeMultipleAmountsFromApi,
  transformTypeMultipleAmountsToApi,
} from "../shared/builders";

import { EARNINGS_TYPES_WITHOUT_AVERAGE_MONTHS } from "./constants";

import type {
  BasicLifeAdndBenefitsApi,
  BasicLifeAdndBenefitsGrid,
  BasicLifeExtendedBenefitApi,
} from "./types";

type BasicLifeGridInput = Partial<BasicLifeAdndBenefitsGrid>;
type BasicLifeApiInput = Partial<BasicLifeAdndBenefitsApi>;
type BasicLifeDefinitionOfEarningsApi = NonNullable<
  BasicLifeAdndBenefitsApi["definitionOfEarnings"]
>;
type DefinitionOfEarningsBuilderResult = {
  definitionOfEarningsType?: string;
  definitionOfEarningsAmount?: number;
};

/**
 * Helper to check if a numeric value is valid (>= 1)
 */
const isValidNumeric = (value: unknown): value is number =>
  typeof value === "number" && value >= 1;

const normalizePortability = (
  portability: BasicLifeGridInput["portability"],
): BasicLifeAdndBenefitsApi["portability"] => {
  if (portability === "includedWithEoi") {
    return "includedWithEOI";
  }

  if (portability === "includedWithoutEoi") {
    return "includedWithoutEOI";
  }

  return portability;
};

const transformMultiAmountBenefitsToApi = (
  benefits: BasicLifeGridInput,
): Pick<
  BasicLifeAdndBenefitsApi,
  "benefitAmount" | "benefitMaximum" | "guaranteeIssue"
> => {
  const transformed: Pick<
    BasicLifeAdndBenefitsApi,
    "benefitAmount" | "benefitMaximum" | "guaranteeIssue"
  > = {};

  if (
    shouldIncludeBenefitInApi(benefits.benefitAmountType) ||
    isValidNumeric(benefits.benefitAmountDollarAmount) ||
    isValidNumeric(benefits.benefitAmountSalaryMultiplier)
  ) {
    transformed.benefitAmount = transformTypeMultipleAmountsToApi(
      benefits.benefitAmountType,
      {
        dollarAmount: benefits.benefitAmountDollarAmount,
        salaryMultiplier: benefits.benefitAmountSalaryMultiplier,
      },
    ) as BasicLifeAdndBenefitsApi["benefitAmount"];
  }

  if (
    shouldIncludeBenefitInApi(benefits.benefitMaximumType) ||
    isValidNumeric(benefits.benefitMaximumDollarAmount) ||
    isValidNumeric(benefits.benefitMaximumSalaryMultiplier)
  ) {
    transformed.benefitMaximum = transformTypeMultipleAmountsToApi(
      benefits.benefitMaximumType,
      {
        dollarAmount: benefits.benefitMaximumDollarAmount,
        salaryMultiplier: benefits.benefitMaximumSalaryMultiplier,
      },
      [
        BaseBasicLifeAdndBenefitBenefitMaximumType.dollarAmount,
        BaseBasicLifeAdndBenefitBenefitMaximumType.salaryMultiplier,
        BaseBasicLifeAdndBenefitBenefitMaximumType.lesserOfDollarAmountOrSalaryMultiplier,
      ],
    ) as BasicLifeAdndBenefitsApi["benefitMaximum"];
  }

  if (
    shouldIncludeBenefitInApi(benefits.guaranteeIssueType) ||
    isValidNumeric(benefits.guaranteeIssueSalaryMultiplier) ||
    isValidNumeric(benefits.guaranteeIssueDollarAmount) ||
    isValidNumeric(benefits.guaranteeIssueAdditionalAmount)
  ) {
    transformed.guaranteeIssue = transformTypeMultipleAmountsToApi(
      benefits.guaranteeIssueType,
      {
        salaryMultiplier: benefits.guaranteeIssueSalaryMultiplier,
        dollarAmount: benefits.guaranteeIssueDollarAmount,
        additionalAmount: benefits.guaranteeIssueAdditionalAmount,
      },
    ) as BasicLifeAdndBenefitsApi["guaranteeIssue"];
  }

  return transformed;
};

const transformDefinitionOfEarningsToApi = (
  benefits: BasicLifeGridInput,
): Pick<BasicLifeAdndBenefitsApi, "definitionOfEarnings"> => {
  if (!shouldIncludeBenefitInApi(benefits.definitionOfEarningsType)) {
    return {};
  }

  const definitionOfEarnings: BasicLifeDefinitionOfEarningsApi = {
    type: benefits.definitionOfEarningsType,
  };

  const earningsTypeDisablesAvgMonths =
    EARNINGS_TYPES_WITHOUT_AVERAGE_MONTHS.includes(
      benefits.definitionOfEarningsType,
    );

  const averageMonths = benefits.definitionOfEarningsAverageMonths;
  const hasAverageMonths =
    typeof averageMonths === "number" && averageMonths >= 0;

  if (!earningsTypeDisablesAvgMonths && hasAverageMonths) {
    definitionOfEarnings.earningsAverageMonths = averageMonths;
  }

  return { definitionOfEarnings };
};

const transformDependentBenefitsToApi = (
  benefits: BasicLifeGridInput,
): Pick<
  BasicLifeAdndBenefitsApi,
  "spouseBenefit" | "childBenefit" | "ageReductionSchedule"
> => {
  const transformed: Pick<
    BasicLifeAdndBenefitsApi,
    "spouseBenefit" | "childBenefit" | "ageReductionSchedule"
  > = {};

  if (
    shouldIncludeBenefitInApi(benefits.spouseBenefitType) ||
    isValidNumeric(benefits.spouseBenefitAmount)
  ) {
    transformed.spouseBenefit = transformTypeAmountToApi(
      benefits.spouseBenefitType,
      benefits.spouseBenefitAmount,
    ) as BasicLifeAdndBenefitsApi["spouseBenefit"];
  }

  if (
    shouldIncludeBenefitInApi(benefits.childBenefitType) ||
    isValidNumeric(benefits.childBenefitAmount)
  ) {
    transformed.childBenefit = transformTypeAmountToApi(
      benefits.childBenefitType,
      benefits.childBenefitAmount,
    ) as BasicLifeAdndBenefitsApi["childBenefit"];
  }

  if (shouldIncludeBenefitInApi(benefits.ageReductionScheduleType)) {
    transformed.ageReductionSchedule = transformAgeReductionScheduleToApi(
      benefits.ageReductionScheduleType,
      benefits.ageReductionScheduleSchedules,
    ) as BasicLifeAdndBenefitsApi["ageReductionSchedule"];
  }

  return transformed;
};

const transformSimpleFieldsToApi = (
  benefits: BasicLifeGridInput,
): Pick<
  BasicLifeAdndBenefitsApi,
  | "portability"
  | "employerContribution"
  | "conversion"
  | "acceleratedDeathBenefit"
  | "waiverOfPremium"
> => {
  const transformed: Pick<
    BasicLifeAdndBenefitsApi,
    | "portability"
    | "employerContribution"
    | "conversion"
    | "acceleratedDeathBenefit"
    | "waiverOfPremium"
  > = {};

  if (shouldIncludeBenefitInApi(benefits.portability)) {
    transformed.portability = normalizePortability(benefits.portability);
  }

  if (shouldIncludeBenefitInApi(benefits.employerContribution)) {
    transformed.employerContribution = benefits.employerContribution;
  }

  if (shouldIncludeBenefitInApi(benefits.conversion)) {
    transformed.conversion = benefits.conversion;
  }

  if (shouldIncludeBenefitInApi(benefits.acceleratedDeathBenefit)) {
    transformed.acceleratedDeathBenefit = benefits.acceleratedDeathBenefit;
  }

  if (shouldIncludeBenefitInApi(benefits.waiverOfPremium)) {
    transformed.waiverOfPremium = benefits.waiverOfPremium;
  }

  return transformed;
};

const transformDefinitionOfEarningsFromApi = (
  definitionOfEarnings: BasicLifeApiInput["definitionOfEarnings"],
): Pick<
  BasicLifeAdndBenefitsGrid,
  "definitionOfEarningsType" | "definitionOfEarningsAverageMonths"
> => {
  if (!definitionOfEarnings || typeof definitionOfEarnings !== "object") {
    return {
      definitionOfEarningsType: undefined,
      definitionOfEarningsAverageMonths: undefined,
    };
  }

  const normalizedForBuilder = {
    type: definitionOfEarnings.type,
    amount: definitionOfEarnings.earningsAverageMonths,
  };

  const result = transformTypeAmountFromApi(
    normalizedForBuilder,
    "definitionOfEarnings",
  ) as DefinitionOfEarningsBuilderResult;

  return {
    definitionOfEarningsType: result.definitionOfEarningsType,
    definitionOfEarningsAverageMonths: result.definitionOfEarningsAmount,
  };
};

const getDependentBenefitsIncluded = (
  benefits: BasicLifeApiInput,
): "yes" | "no" | undefined => {
  // No spouse or child benefit at all means nobody answered the question (a
  // rates-only document, a blank plan), distinct from a dependent benefit saved
  // as notIncluded. Answering "no" would show a value nobody entered.
  if (!benefits.spouseBenefit && !benefits.childBenefit) {
    return undefined;
  }

  const spouseBenefitType = benefits.spouseBenefit?.type;
  const childBenefitType = benefits.childBenefit?.type;
  const hasSpouseBenefits = Boolean(
    spouseBenefitType && spouseBenefitType !== "notIncluded",
  );
  const hasChildBenefits = Boolean(
    childBenefitType && childBenefitType !== "notIncluded",
  );

  return hasSpouseBenefits || hasChildBenefits ? "yes" : "no";
};

/**
 * Transforms Basic Life benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures for:
 * - Benefit amount (type, dollar amount, salary multiplier)
 * - Benefit maximum (type, amount)
 * - Guarantee issue (type, salary multiplier, dollar amount, additional amount)
 * - Definition of earnings (type and average months)
 * - Spouse benefit (type, amount)
 * - Child benefit (type, amount)
 * - Age reduction schedule (type and schedule details)
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformBasicLifeBenefitsToApi = (
  benefits: BasicLifeGridInput,
): BasicLifeAdndBenefitsApi => {
  const transformed: BasicLifeAdndBenefitsApi = {
    objectType: "basicLifeAdnd",
    ...transformMultiAmountBenefitsToApi(benefits),
    ...transformDefinitionOfEarningsToApi(benefits),
    ...transformDependentBenefitsToApi(benefits),
    ...transformSimpleFieldsToApi(benefits),
  };

  // Extended (non-v5) fields — CD-1662. Sent as a full object (a full-replace
  // PATCH, like the rest of `benefits`) whenever at least one extended field
  // is set, so an edit to any other field on the plan doesn't drop the
  // extended values already on it. Omitted entirely when every extended
  // field is unset, so an edit that never touches these fields doesn't
  // create an empty extended row on the backend.
  const extended = {
    eligibilityHours: benefits.eligibilityHours,
    // These three are edited through a numeric cell (CD-2412), so AG Grid
    // commits a number even though the backend field is a Go string.
    waiverOfPremiumEligibility: toExtendedStringField(
      benefits.waiverOfPremiumEligibility,
    ),
    waiverOfPremiumWaitingPeriod: toExtendedStringField(
      benefits.waiverOfPremiumWaitingPeriod,
    ),
    waiverOfPremiumDuration: toExtendedStringField(
      benefits.waiverOfPremiumDuration,
    ),
    airBag: benefits.airBag,
    commonCarrier: benefits.commonCarrier,
    repatriation: benefits.repatriation,
    seatBelt: benefits.seatBelt,
    eapFaceToFaceVisitsLimitType: benefits.eapFaceToFaceVisitsLimitType,
    eapVisitCount: benefits.eapVisitCount,
    travelAid: benefits.travelAid,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return cleanupNotDisclosedBenefits(transformed);
};

/**
 * Transforms Basic Life benefits from API format to frontend grid format
 *
 * Converts nested API structures into flat grid fields for:
 * - Benefit amount (type, dollar amount, salary multiplier)
 * - Benefit maximum (type, amount)
 * - Guarantee issue (type, salary multiplier, dollar amount, additional amount)
 * - Definition of earnings (type and average months)
 * - Spouse benefit (type, amount)
 * - Child benefit (type, amount)
 * - Age reduction schedule (type and schedule details)
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object matching the grid structure (flat fields)
 */
export const transformBasicLifeBenefitsFromApi = (
  benefits: BasicLifeApiInput | null | undefined,
): BasicLifeAdndBenefitsGrid => {
  if (!benefits) return { objectType: "basicLifeAdnd" };

  const transformed: BasicLifeAdndBenefitsGrid = {
    objectType: "basicLifeAdnd",
  };

  // Transform BenefitAmount from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      benefits.benefitAmount,
      "benefitAmount",
      ["dollarAmount", "salaryMultiplier"],
    ) as Pick<
      BasicLifeAdndBenefitsGrid,
      | "benefitAmountType"
      | "benefitAmountDollarAmount"
      | "benefitAmountSalaryMultiplier"
    >,
  );

  // Transform BenefitMaximum from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      benefits.benefitMaximum,
      "benefitMaximum",
      ["dollarAmount", "salaryMultiplier"],
    ) as Pick<
      BasicLifeAdndBenefitsGrid,
      | "benefitMaximumType"
      | "benefitMaximumDollarAmount"
      | "benefitMaximumSalaryMultiplier"
    >,
  );

  // Transform GuaranteeIssue from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      benefits.guaranteeIssue,
      "guaranteeIssue",
      ["salaryMultiplier", "dollarAmount", "additionalAmount"],
    ) as Pick<
      BasicLifeAdndBenefitsGrid,
      | "guaranteeIssueType"
      | "guaranteeIssueSalaryMultiplier"
      | "guaranteeIssueDollarAmount"
      | "guaranteeIssueAdditionalAmount"
    >,
  );

  // Transform DefinitionOfEarnings from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformDefinitionOfEarningsFromApi(benefits.definitionOfEarnings),
  );

  transformed.dependentBenefitsIncluded =
    getDependentBenefitsIncluded(benefits);

  // Transform SpouseBenefit from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.spouseBenefit, "spouseBenefit") as Pick<
      BasicLifeAdndBenefitsGrid,
      "spouseBenefitType" | "spouseBenefitAmount"
    >,
  );

  // Transform ChildBenefit from API object to grid fields - use builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.childBenefit, "childBenefit") as Pick<
      BasicLifeAdndBenefitsGrid,
      "childBenefitType" | "childBenefitAmount"
    >,
  );

  // Transform ageReductionSchedule from API object to grid fields
  Object.assign(
    transformed,
    transformAgeReductionScheduleFromApi(benefits.ageReductionSchedule) as Pick<
      BasicLifeAdndBenefitsGrid,
      "ageReductionScheduleType" | "ageReductionScheduleSchedules"
    >,
  );

  // Simple field mappings
  transformed.portability = benefits.portability;
  transformed.conversion = benefits.conversion;
  transformed.acceleratedDeathBenefit = benefits.acceleratedDeathBenefit;
  transformed.waiverOfPremium = benefits.waiverOfPremium;
  transformed.employerContribution = benefits.employerContribution;

  // Extended (non-v5) fields — CD-1662
  Object.assign(
    transformed,
    transformBasicLifeExtendedFromApi(benefits.extended),
  );

  return transformed;
};

type BasicLifeExtendedGridFields = Pick<
  BasicLifeAdndBenefitsGrid,
  | "eligibilityHours"
  | "waiverOfPremiumEligibility"
  | "waiverOfPremiumWaitingPeriod"
  | "waiverOfPremiumDuration"
  | "airBag"
  | "commonCarrier"
  | "repatriation"
  | "seatBelt"
  | "eapFaceToFaceVisitsLimitType"
  | "eapVisitCount"
  | "travelAid"
>;

const BASIC_LIFE_EXTENDED_FIELD_KEYS: (keyof BasicLifeExtendedGridFields)[] = [
  "eligibilityHours",
  "waiverOfPremiumEligibility",
  "waiverOfPremiumWaitingPeriod",
  "waiverOfPremiumDuration",
  "airBag",
  "commonCarrier",
  "repatriation",
  "seatBelt",
  "eapFaceToFaceVisitsLimitType",
  "eapVisitCount",
  "travelAid",
];

// Extended (non-v5) fields — CD-1662. Split out of
// transformBasicLifeBenefitsFromApi to keep that function's complexity down.
// Grid and API field names match 1:1, so a single loop over the shared key
// list replaces one `?? undefined` assignment per field. Every key is
// present (as undefined) even when `extended` itself is absent, so a later
// edit to any other field doesn't leave a stale extended value in the grid
// row from a previous plan.
const transformBasicLifeExtendedFromApi = (
  extended: BasicLifeExtendedBenefitApi | undefined,
): BasicLifeExtendedGridFields =>
  Object.fromEntries(
    BASIC_LIFE_EXTENDED_FIELD_KEYS.map((key) => [
      key,
      extended?.[key] ?? undefined,
    ]),
  ) as BasicLifeExtendedGridFields;

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const BASIC_LIFE_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  BASIC_LIFE_EXTENDED_FIELD_KEYS,
);
